import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { isProcessingInvitationAtom } from "@/store/invitationAtoms";

// Onboarding invitation endpoints (#331, E5-S5). The token is an opaque,
// 256-bit, single-use value carried in the signup-link URL; it is placed in a
// path segment here, so it is URL-encoded.
const ONBOARDING_INVITE_ENDPOINT = "/onboarding/invite";
const INVITATIONS_ENDPOINT = "/onboarding/invitations";

export interface ValidateInvitationResponse {
    email: string;
    organizationName: string;
}

export const useInvitationApi = () => {
    const { callApi } = useApi();
    const setIsProcessing = useSetAtom(isProcessingInvitationAtom);

    // Anonymous — the applicant has no account yet, so skip the bearer token.
    // skipErrorNotification: the landing page renders its own valid/invalid
    // states, so the generic toast would double up (same posture as the apply
    // form). An invalid/expired/consumed token surfaces as a rejected promise.
    const validateInvitation = useCallback(
        (token: string): Promise<ValidateInvitationResponse> =>
            callApi<ValidateInvitationResponse>(
                `${ONBOARDING_INVITE_ENDPOINT}/${encodeURIComponent(token)}`,
                {
                    method: "GET",
                    skipAuth: true,
                    skipErrorNotification: true,
                },
            ),
        [callApi],
    );

    // The applicant supplies the password only; name/email/address come from the
    // invitation the API captured at /apply. On 200 the Entra user exists and is
    // linked to the pending org — the caller then signs in normally.
    const completeInvitation = useCallback(
        async (token: string, password: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi<void>(
                    `${ONBOARDING_INVITE_ENDPOINT}/${encodeURIComponent(token)}/complete`,
                    {
                        method: "POST",
                        body: JSON.stringify({ password }),
                        skipAuth: true,
                        skipErrorNotification: true,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // "Send me a new link" from the invalid-link page (#438 / OB-02).
    // Anonymous. Every well-formed email gets the same body-less 202 — the API
    // never says whether it has an application — and a malformed one a 400.
    // `turnstileToken` is Cloudflare Turnstile's single-use token (OB-06);
    // JSON.stringify drops it while undefined, so the body is then { email }.
    const resendInvitation = useCallback(
        async (email: string, turnstileToken?: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi<void>(INVITATIONS_ENDPOINT, {
                    method: "POST",
                    body: JSON.stringify({ email, turnstileToken }),
                    skipAuth: true,
                    skipErrorNotification: true,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    return { validateInvitation, completeInvitation, resendInvitation };
};
